import { ContainerEnvironment } from '@fluidx/office-fluid-types';
import { FluidCodeLoaderObjectStoreName, getCodeLoaderCache, getCodeLoaderCacheKey } from './CodeLoaderCache';
import { ContainerCodeDetailsProvider, ContainerCodeLoadDetails, IVersionedFluidBrowserPackage } from './types';
import { ITelemetryBaseLogger, ITelemetryProperties } from '@fluidframework/common-definitions';
import { ActivityTracker, getDocumentHiddenResult, startDocumentHiddenTracker } from '@fluidx/utilities';
import { areAllCodeUrlsAllowed } from './isAllowedCodeUrl';
import { OfficeContainerCodeLoadDetails } from './FFXCodeDetailsHelpers';

// Cache container details for up to a week
const maxCacheAgeMs = 7 * 24 * 60 * 60 * 1000;

type GetContainerDetailsFromCacheResult = {
  // Metadata about the cache lookup that may be useful for logging
  metadata: {
    // Status for the cache for this look up, currently it is hit/miss/expired/unsafe
    cacheStatus: string;
    // The age for a specific entry in the cache
    cacheAge?: number;
  };
  codeDetails: IVersionedFluidBrowserPackage | undefined;
};

/** Gets any unexpired container code details from the indexeddb cache for the given package */
async function getContainerDetailsFromCache(
  packageName: string,
  containerEnvironment: ContainerEnvironment,
  allowedCodeOrigins: string[]
): Promise<GetContainerDetailsFromCacheResult> {
  try {
    const db = await getCodeLoaderCache();
    const result = await db.get(
      FluidCodeLoaderObjectStoreName,
      getCodeLoaderCacheKey({ packageName, containerEnvironment })
    );
    db.close();

    if (result) {
      const cacheEntryAge = Date.now() - result.lastUpdatedTime;

      if (cacheEntryAge > maxCacheAgeMs) {
        return {
          metadata: { cacheStatus: 'expired', cacheAge: cacheEntryAge },
          codeDetails: undefined
        };
      }

      if (!areAllCodeUrlsAllowed(result.codeDetailsPackage.fluid.browser.umd.files, allowedCodeOrigins)) {
        // If the code referenced in the cache is not in the allow list, let's not return it so that we can favor getting it from another source, like the network
        // Also, delete the cache entry so that it isn't used again in the future
        deleteContainerDetailsFromCache(packageName, containerEnvironment).catch(() => {});

        return {
          metadata: { cacheStatus: 'unsafe' },
          codeDetails: undefined
        };
      }

      return {
        metadata: {
          cacheStatus: 'hit',
          cacheAge: cacheEntryAge
        },
        codeDetails: result.codeDetailsPackage
      };
    }
  } catch {}

  return { metadata: { cacheStatus: 'miss' }, codeDetails: undefined };
}

async function deleteContainerDetailsFromCache(packageName: string, containerEnvironment: ContainerEnvironment) {
  try {
    const db = await getCodeLoaderCache();
    await db.delete(FluidCodeLoaderObjectStoreName, getCodeLoaderCacheKey({ packageName, containerEnvironment }));
    db.close();
  } catch {}
}

/**
 * Gets container code details from the network, storing the response in the cache.
 * @returns codeDetailsPromise that resolves right when the code details are available from the network and a cacheUpdatePromise that resolves when the local cache is updated
 */
function getCodeDetailsFromNetworkAndUpdateCache(
  packageName: string,
  containerEnvironment: ContainerEnvironment,
  getCodeDetailsFromNetwork: CachableCodeDetailsProvider['getLatestContainerCodeDetails'],
  preCacheContainerCode: CachableCodeDetailsProvider['preCacheContainerCode'],
  locale: string | undefined
): { codePackagePromise: Promise<IVersionedFluidBrowserPackage>; cacheUpdatePromise: Promise<void> } {
  const codeDetailsPromise = getCodeDetailsFromNetwork(containerEnvironment);

  const cacheUpdatePromise = codeDetailsPromise
    .then(async (latestCodeDetails) => {
      const db = await getCodeLoaderCache();
      const packageKey = getCodeLoaderCacheKey({ packageName, containerEnvironment });
      const existingEntry = await db.get(FluidCodeLoaderObjectStoreName, packageKey);

      // If the new code details don't match what we already have cached, preload the new version for improved performance on subsequent loads
      if (
        !existingEntry ||
        existingEntry.codeDetailsPackage.version !== latestCodeDetails.fluidCodeDetailsOverride.package.version
      ) {
        preCacheContainerCode(latestCodeDetails, locale);
      }

      // Always update the last update time. This will keep us from prematurely expiring the cache when a particular build is in production for a long time
      await db.put(
        FluidCodeLoaderObjectStoreName,
        {
          codeDetailsPackage: latestCodeDetails.fluidCodeDetailsOverride.package,
          lastUpdatedTime: Date.now()
        },
        packageKey
      );

      db.close();
    })
    .catch(() => {});

  return {
    codePackagePromise: codeDetailsPromise.then((details) => {
      // Attach max unsupported version to package if available.
      const maxUnsupportedVersion = (details as OfficeContainerCodeLoadDetails).maxUnsupportedContainerScriptVersion;
      return {
        ...details.fluidCodeDetailsOverride.package,
        maxUnsupportedVersion
      };
    }),
    cacheUpdatePromise
  };
}

/** Gets container code details from the faster of the cache or network */
async function getCodeDetailsPackageFromCacheOrNetwork(
  packageName: string,
  containerEnvironment: ContainerEnvironment,
  allowedCodeOrigins: string[],
  getCodeDetailsFromNetwork: ContainerCodeDetailsProvider['getCodeDetailsPackage'],
  logger?: ITelemetryBaseLogger
): Promise<IVersionedFluidBrowserPackage> {
  const documentEverHiddenTrackerId = startDocumentHiddenTracker();
  const tracker = ActivityTracker.start('GetCodeDetailsPackage', logger, { addUserTiming: true });

  const cachePromise = getContainerDetailsFromCache(packageName, containerEnvironment, allowedCodeOrigins).then(
    (cacheDetails) => ({
      method: 'cache' as const,
      cacheDetails
    })
  );
  const networkPromise = getCodeDetailsFromNetwork(containerEnvironment, allowedCodeOrigins).then((codeDetails) => ({
    method: 'network' as const,
    codeDetails
  }));

  const raceWinner = await Promise.race([cachePromise, networkPromise]);

  // Additional metadata to log in the ActivityTracker setResult call
  let additionalActivityProperties: ITelemetryProperties = {
    documentEverHidden: getDocumentHiddenResult(documentEverHiddenTrackerId, true)! > -1
  };
  if (raceWinner.method === 'cache') {
    const { cacheDetails } = raceWinner;
    additionalActivityProperties = { ...additionalActivityProperties, ...cacheDetails.metadata };
    if (cacheDetails.codeDetails) {
      tracker.setResult(true, { method: 'cache', ...additionalActivityProperties });
      return cacheDetails.codeDetails;
    }
  }

  return networkPromise.then((response) => {
    tracker.setResult(true, { method: 'network', ...additionalActivityProperties });
    return response.codeDetails;
  });
}

/** Interface that is very similar to CodeDetailsProvider, but provides inputs in a slightly different format to facilitate caching */
export interface CachableCodeDetailsProvider {
  /** A function that gets the latest code details for a package*/
  getLatestContainerCodeDetails: (containerEnvironment: ContainerEnvironment) => Promise<ContainerCodeLoadDetails>;

  /** A function that will pre fetch all the critical container code for a specific version */
  preCacheContainerCode: (containerCodeLoadDetails: ContainerCodeLoadDetails, locale: string | undefined) => void;

  /**
   * An optional API that can be invoked periodically to see if the currently running version of the container is supported. Returning false implies that the currently running session should be ended.
   * @param {currentVersion} - the currently running version of the code for the container
   */
  upgradeNeeded?: (containerEnvironment: ContainerEnvironment, currentVersion: string) => Promise<boolean>;
}

/**
 * Creates a code details provider that fetches code details from a slow source (like a network), storing the data in a cache for faster access.
 * Every time we get the container code, we favor the version in cache if it is less than a week old. Every call to getCodeDetails updates the value in the cache for that container.
 */
export function createCodeDetailsProviderWithCache(
  packageName: string,
  codeDetailsProvider: CachableCodeDetailsProvider,
  logger?: ITelemetryBaseLogger,
  localeHint?: string
): ContainerCodeDetailsProvider {
  const {
    getLatestContainerCodeDetails,
    upgradeNeeded: baseUpgradeNeeded,
    preCacheContainerCode
  } = codeDetailsProvider;
  const curriedGetCodeDetailsFromNetworkAndUpdateCache = (containerEnvironment: ContainerEnvironment) =>
    getCodeDetailsFromNetworkAndUpdateCache(
      packageName,
      containerEnvironment,
      getLatestContainerCodeDetails,
      preCacheContainerCode,
      localeHint
    );

  return {
    getCodeDetailsPackage: (containerEnvironment, allowedCodeOrigins) =>
      getCodeDetailsPackageFromCacheOrNetwork(
        packageName,
        containerEnvironment,
        allowedCodeOrigins,
        (containerEnvironment) =>
          // We don't need to block on the cache update promise here, we want to return the code details as soon as possible
          curriedGetCodeDetailsFromNetworkAndUpdateCache(containerEnvironment).codePackagePromise,
        logger
      ),
    upgradeNeeded: async (containerEnvironment, currentVersion) => {
      // If there is no baseUpgradeNeeded function, we assume no upgrade is needed
      if (!baseUpgradeNeeded) {
        return false;
      }
      const shouldUpgrade = await baseUpgradeNeeded(containerEnvironment, currentVersion);

      if (shouldUpgrade) {
        // If we should upgrade, let's make sure that we clear out the current cached code details to make sure we get the latest on the next load.
        await deleteContainerDetailsFromCache(packageName, containerEnvironment);

        // Let's opportunistically pre cache the latest code details to improve performance for the expected forced reload, but don't wait on the result
        curriedGetCodeDetailsFromNetworkAndUpdateCache(containerEnvironment);
      }

      return shouldUpgrade;
    },
    preCacheLatestContainerCode: async (containerEnvironment) =>
      await curriedGetCodeDetailsFromNetworkAndUpdateCache(containerEnvironment).cacheUpdatePromise
  };
}
